/**
 * Controls (08 §3-4), reached from Options: every action of the active scheme with its keys,
 * each key a button that opens "PRESS A KEY" to rebind it; the scheme switch; RESET TO DEFAULTS,
 * which asks first. The bindings belong to the input layer - this screen only talks to the
 * `BindingsPort` the app injected, and says so plainly when there is none.
 */

import './rebind.css';
import type { Ctx, Screen } from '../context';
import { box, button, el, errorMessage, panel, show } from '../dom';
import { choiceRow } from '../widgets/choiceRow';
import { confirm } from '../widgets/confirm';
import { frame } from '../widgets/frame';
import type { BindingsPort, BoundAction, ControlScheme } from './bindingsPort';
import { openCapture } from './captureDialog';
import { keyName, UNBOUND } from './keyNames';

/** Every action shows at least this many slots; an empty one reads `---` and can be filled. */
export const KEY_SLOTS = 2;

const SCHEMES: ReadonlyArray<{ value: ControlScheme; label: string; title: string }> = [
  { value: 'modern', label: 'MODERN', title: 'WASD AND THE MOUSE: MOVE AND AIM INDEPENDENTLY.' },
  { value: 'classic', label: 'CLASSIC', title: 'THE 1987 FEEL: 8-WAY MOVEMENT, 32-STEP AIM, A MILD AIM ASSIST.' },
];

function unavailable(ctx: Ctx): Screen {
  const f = frame('controls', 'CONTROLS');
  const back = button('BACK', () => ctx.nav.options());
  f.body.append(
    box(
      'menu-col menu-grow menu-rebind-none',
      el('div', 'menu-prose menu-paper', 'KEY BINDINGS ARE NOT AVAILABLE HERE: THE GAME HAS NOT HANDED THE MENUS ITS'
        + ' CONTROLS. THE DEFAULT KEYS APPLY.'),
      box('menu-row', back),
    ),
  );
  return { el: f.el, back: () => ctx.nav.options(), initialFocus: () => back };
}

export function controlsScreen(ctx: Ctx): Screen {
  const given = ctx.bindings;
  if (given === undefined) return unavailable(ctx);
  const port: BindingsPort = given;

  const f = frame('controls', 'CONTROLS');
  const table = panel('ACTIONS', 'menu-grow menu-rebind-table');
  const message = el('div', 'menu-rebind-message menu-prose menu-hidden');
  message.setAttribute('role', 'status');
  const slots = new Map<string, HTMLButtonElement>();
  let stopCapture: (() => void) | null = null;

  const say = (text: string, bad = false): void => {
    message.textContent = bad ? `! ${text}` : text;
    message.classList.toggle('menu-error', bad);
    message.classList.toggle('menu-good', !bad && text !== '');
    show(message, text !== '');
  };

  /** Every call into the port is guarded: whatever it throws is said, not swallowed. */
  const guarded = <T>(run: () => T, fallback: T): T => {
    try {
      return run();
    } catch (err) {
      say(errorMessage(err), true);
      return fallback;
    }
  };

  const rebind = (action: BoundAction, slot: number): void => {
    stopCapture?.();
    stopCapture = openCapture(ctx, {
      actionLabel: action.label,
      slot,
      current: action.keys[slot] ?? '',
      tryBind: (code) => guarded(() => port.rebind(action.id, slot, code), 'THE KEY COULD NOT BE BOUND'),
      onClose: (outcome) => {
        stopCapture = null;
        if (outcome === 'cancelled') return;
        render();
        say(`${action.label}: ${outcome === 'cleared' ? 'SLOT CLEARED' : 'BOUND'}.`);
        slots.get(`${action.id}:${slot}`)?.focus();
      },
    });
  };

  function render(): void {
    slots.clear();
    const actions = guarded(() => port.actions(), [] as ReadonlyArray<BoundAction>);
    const rows = actions.map((action) => {
      const row = box('menu-rebind-row', el('span', 'menu-rebind-label', action.label));
      for (let slot = 0; slot < Math.max(KEY_SLOTS, action.keys.length); slot++) {
        const code = action.keys[slot] ?? '';
        const shown = code === '' ? UNBOUND : keyName(code);
        const b = button(shown, () => rebind(action, slot), 'menu-btn-small menu-rebind-key');
        b.setAttribute('aria-label', `REBIND ${action.label}, SLOT ${slot + 1}: ${code === '' ? 'EMPTY' : shown}`);
        slots.set(`${action.id}:${slot}`, b);
        row.append(b);
      }
      return row;
    });
    table.body.replaceChildren(...rows);
    if (rows.length === 0) table.body.append(el('div', 'menu-dim', 'THIS SCHEME HAS NO ACTIONS TO BIND.'));
  }

  const scheme = choiceRow<ControlScheme>('SCHEME', SCHEMES, guarded(() => port.scheme(), 'modern'), (value) => {
    guarded(() => port.setScheme(value), undefined);
    say('');
    render();
  });

  const reset = button(
    'RESET TO DEFAULTS',
    () =>
      confirm(ctx, {
        title: 'RESET TO DEFAULTS',
        lines: [
          `EVERY KEY OF THE ${scheme.value().toUpperCase()} SCHEME GOES BACK TO ITS DEFAULT.`,
          'YOUR OWN BINDINGS FOR IT ARE LOST. THE OTHER SCHEME IS NOT TOUCHED.',
        ],
        confirmLabel: 'RESET',
        danger: true,
        onConfirm: () => {
          guarded(() => port.reset(), undefined);
          scheme.set(guarded(() => port.scheme(), 'modern'));
          render();
          say('DEFAULTS RESTORED.');
          reset.focus();
        },
      }),
    'menu-btn-danger',
  );
  const back = button('BACK', () => ctx.nav.options());
  render();

  const side = box(
    'menu-col menu-rebind-side',
    scheme.el,
    el('div', 'menu-prose menu-dim menu-small', 'SELECT A KEY, THEN PRESS ITS REPLACEMENT. ESC CANCELS. A KEY'
      + ' ALREADY IN USE IS REFUSED WITH THE REASON.'),
    message,
    box('menu-rebind-actions menu-col', reset, back),
  );
  f.body.append(table.panel, side);
  return {
    el: f.el,
    back: () => ctx.nav.options(),
    initialFocus: () => back,
    dispose: () => stopCapture?.(),
  };
}
